import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Clock, Zap, X } from "lucide-react";
import { crmApi } from "../../lib/crm";

const ICONS = {
  flame: Flame,
  clock: Clock,
  zap: Zap,
};

const SEVERITY_STYLES = {
  red: "from-rose-500/15 to-red-500/10 border-rose-500/40 text-rose-200",
  amber: "from-amber-500/15 to-orange-500/10 border-amber-500/40 text-amber-200",
  blue: "from-sky-500/15 to-blue-500/10 border-sky-500/40 text-sky-200",
};
const SEVERITY_ICONS = {
  red: "text-rose-300",
  amber: "text-amber-300",
  blue: "text-sky-300",
};

/**
 * Floating nudge banner shown to AGENTS at the top of the CRM.
 * Pulls /api/crm/agent/nudges every 2 minutes and displays up to 3
 * personalized prompts (idle leads, overdue tasks, no actions today).
 * Each card can be temporarily dismissed (session-scoped).
 */
export default function AgentNudgeBanner({ enabled }) {
  const [nudges, setNudges] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem("gtx_nudges_dismissed") || "[]"));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    if (!enabled) return undefined;
    let stop = false;
    const load = async () => {
      try {
        const r = await crmApi.get("/agent/nudges");
        if (!stop) setNudges(r.data?.nudges || []);
      } catch {
        /* silent */
      }
    };
    load();
    const id = setInterval(load, 120_000);
    return () => { stop = true; clearInterval(id); };
  }, [enabled]);

  if (!enabled) return null;

  const visible = nudges.filter((n) => !dismissed.has(n.code || n.title));
  if (visible.length === 0) return null;

  const dismiss = (n) => {
    const key = n.code || n.title;
    const next = new Set(dismissed);
    next.add(key);
    setDismissed(next);
    sessionStorage.setItem("gtx_nudges_dismissed", JSON.stringify([...next]));
  };

  return (
    <div className="space-y-2 mb-4" data-testid="agent-nudge-banner">
      {visible.map((n) => {
        const Icon = ICONS[n.icon] || Zap;
        return (
          <div
            key={n.code || n.title}
            className={`relative rounded-xl border bg-gradient-to-r p-4 pr-12 ${SEVERITY_STYLES[n.severity] || SEVERITY_STYLES.amber}`}
            data-testid={`nudge-${n.code || "x"}`}
          >
            <button
              type="button"
              onClick={() => dismiss(n)}
              aria-label="Dismiss"
              data-testid={`nudge-dismiss-${n.code || "x"}`}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-md hover:bg-white/10 text-white/40 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
              <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${SEVERITY_ICONS[n.severity] || SEVERITY_ICONS.amber}`} />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-white">{n.title}</div>
                <p className="text-xs text-white/70 mt-0.5">{n.subtitle}</p>
                {n.cta && n.link && (
                  <Link
                    to={n.link}
                    data-testid={`nudge-cta-${n.code || "x"}`}
                    className="inline-block mt-2 text-xs font-semibold px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white border border-white/15"
                  >
                    {n.cta} →
                  </Link>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
